import React, {useState} from 'react';
import {
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Paper,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  TextField,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import Iconify from 'src/minimal-components/iconify';
import EmptyContent from '@components/empty-content/empty-content';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

interface Condition {
  id: number;
  attribute: string;
  value: string;
}

interface Rule {
  id: number;
  conditions: Condition[];
}
const attributeOptions = [
  'Designation',
  'Location',
  'Department',
  'Role',
  'User',
  'Group',
  'Experience',
  'Skills'
];
const ApplicableToPage: React.FC = () => {
  const [courseAccess, setCourseAccess] = useState('accessAnywhere');
  const [rules, setRules] = useState<Rule[]>([]);
  const [coursetrue, setCourseTrue] = useState(true);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [ruleToDelete, setRuleToDelete] = useState<number | null>(null);

  const handleAttributeChange = (
    ruleId: number,
    conditionId: number,
    attribute: string
  ) => {
    setRules(
      rules.map(rule =>
        rule.id === ruleId
          ? {
              ...rule,
              conditions: rule.conditions.map(condition =>
                condition.id === conditionId
                  ? {...condition, attribute}
                  : condition
              )
            }
          : rule
      )
    );
  };

  const handleValueChange = (
    ruleId: number,
    conditionId: number,
    value: string
  ) => {
    setRules(
      rules.map(rule =>
        rule.id === ruleId
          ? {
              ...rule,
              conditions: rule.conditions.map(condition =>
                condition.id === conditionId ? {...condition, value} : condition
              )
            }
          : rule
      )
    );
  };

  const handleAddCondition = (ruleId: number) => {
    setRules(
      rules.map(rule =>
        rule.id === ruleId
          ? {
              ...rule,
              conditions: [
                ...rule.conditions,
                {id: Date.now(), attribute: '', value: ''}
              ]
            }
          : rule
      )
    );
  };

  const handleRemoveCondition = (ruleId: number, conditionId: number) => {
    setRules(
      rules.map(rule =>
        rule.id === ruleId
          ? {
              ...rule,
              conditions: rule.conditions.filter(
                condition => condition.id !== conditionId
              )
            }
          : rule
      )
    );
  };

  const handleAddRule = () => {
    setRules([
      ...rules,
      {id: Date.now(), conditions: [{id: Date.now(), attribute: '', value: ''}]}
    ]);
  };

  const handleCourseAccessChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCourseAccess((event.target as HTMLInputElement).value);
    if ((event.target as HTMLInputElement).value === 'accessAnywhere') {
      setCourseTrue(true);
    } else {
      setCourseTrue(false);
    }
  };

  const handleCreateRule = () => {
    // Logic to create a new rule
    setRules([
      {id: Date.now(), conditions: [{id: Date.now(), attribute: '', value: ''}]}
    ]);
  };

  const handleSave = () => {
    // Logic to save the form
    console.log({courseAccess, rules});
  };
  const deleteRule = (ruleId: number) => {
    setRuleToDelete(ruleId);
    setOpenDialog(true);
  };

  const confirmdeleteRule = () => {
    if (ruleToDelete !== null) {
      setRules(rules.filter(rule => rule.id !== ruleToDelete));
      setRuleToDelete(null);
      setOpenDialog(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <>
      <Container
        sx={{
          backgroundColor: 'white',
          borderRadius: '8px',
          maxWidth: '100% !important',
          py: '3vh'
        }}>
        <Grid
          container
          spacing={2}
          sx={{margin: '0 !important', width: '100% !important'}}>
          <Grid item xs={12} sx={{pb: '1vh'}}>
            <FormControl component="fieldset">
              <h3>Course Access</h3>
              <p style={{fontSize: 'x-small', color: 'grey'}}>
                Choose if there is direct access to the course or only through a
                Learning plan
              </p>
              <RadioGroup
                row
                aria-label="course access"
                name="courseAccess"
                value={courseAccess}
                onChange={handleCourseAccessChange}>
                <FormControlLabel
                  value="accessAnywhere"
                  control={<Radio />}
                  label="Access anywhere"
                />
                <FormControlLabel
                  value="throughLearningPlan"
                  control={<Radio />}
                  label="Through Learning Plan"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {coursetrue && (
            <Grid item xs={12}>
              <h3>Rule</h3>
              <p style={{fontSize: 'x-small', color: 'grey'}}>
                Create rules that help you refine the applicable group for each
                course. You can create more than one rule and each rule will
                apply independently when all its conditions are met.
              </p>
              {/* Render your rules here */}
              {rules.length === 0 ? (
                <Grid sx={{pb: '2vh'}}>
                  <EmptyContent
                    sx={{pt: 1}}
                    title="No rules added yet. Click Create Rule below to start defining rules"
                  />
                  <div
                    style={{
                      display: 'flex',
                      padding: '2vh',
                      justifyContent: 'center'
                    }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleCreateRule}>
                      + Create Rule
                    </Button>
                  </div>
                </Grid>
              ) : (
                <Grid container spacing={2} sx={{padding: 4}}>
                  {rules.map((rule, ruleIndex) => (
                    <Grid item xs={12} key={rule.id}>
                      <Paper sx={{padding: 2}}>
                        <div>
                          <Typography variant="h6">
                            Rule {ruleIndex + 1}
                            <IconButton
                              onClick={() => deleteRule(rule.id)}
                              sx={{color: 'error.main'}}>
                              <Iconify icon="solar:trash-bin-trash-bold" />
                            </IconButton>
                          </Typography>
                        </div>
                        {rule.conditions.map((condition, conditionIndex) => (
                          <Grid container spacing={1} key={condition.id} sx={{pb:'1vh'}}>
                            <Grid item xs={5}>
                              <FormControl fullWidth>
                                <InputLabel>Attribute</InputLabel>
                                <Select
                                  value={condition.attribute}
                                  label="Attribute"
                                  onChange={e =>
                                    handleAttributeChange(
                                      rule.id,
                                      condition.id,
                                      e.target.value
                                    )
                                  }>
                                  {attributeOptions.map(option => (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={5}>
                              <TextField
                                label="Value"
                                value={condition.value}
                                onChange={e =>
                                  handleValueChange(
                                    rule.id,
                                    condition.id,
                                    e.target.value
                                  )
                                }
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={2}>
                              <IconButton
                                onClick={() => handleAddCondition(rule.id)}>
                                <AddCircleOutlineIcon />
                              </IconButton>
                              {rule.conditions.length > 1 && (
                                <IconButton
                                  onClick={() =>
                                    handleRemoveCondition(rule.id, condition.id)
                                  }>
                                  <RemoveCircleOutlineIcon />
                                </IconButton>
                              )}
                            </Grid>
                          </Grid>
                        ))}
                      </Paper>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                      variant="contained"
                      onClick={handleAddRule}>
                      Add New Rule
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{marginRight: '2vh'}}>
              Save
            </Button>
            <Button
              onClick={() => {
                /* Cancel logic here */
              }}>
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Container>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Delete Rule</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this rule?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant='outlined'>
            Cancel
          </Button>
          <Button onClick={confirmdeleteRule} variant="contained" color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApplicableToPage;
